import { useEffect, useEffectEvent, useRef, useState } from "react";
import { ApiError, type ApiClient } from "../../api/client";
import type { Authorization, CodePurpose } from "../../api/types";
import { t } from "../../i18n";
import { Dialog } from "../../ui/Dialog";

/** A short pause after the last digit, so the last dot is seen before the code is checked. */
const CHECK_DELAY_MS = 120;
const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "⌫", "0", "OK"] as const;
type Key = (typeof KEYS)[number];

interface CodeWindowProps {
  title: string;
  /** Who should enter a code, under the title ("Eigenaar of manager geeft zijn code in."). */
  note?: string | undefined;
  /** Number of digits: 4 for a personal code, 6 for the special admin code from Tafo. */
  length: number;
  /** Checks the code; a rejection shows its message and empties the dots for another try. */
  submit: (code: string) => Promise<void>;
  onClose: () => void;
}

/**
 * The code window of the prototype: dots and a keypad. Typed digits go to this window only, never to
 * the screen behind it; the code is checked as soon as the last digit is in.
 */
export function CodeWindow({ title, note, length, submit, onClose }: CodeWindowProps) {
  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const checking = useRef(false);

  const check = async (entered: string) => {
    if (checking.current) return;
    checking.current = true;
    setBusy(true);
    try {
      await submit(entered);
    } catch (error) {
      setCode("");
      setMessage(error instanceof ApiError ? error.message : t.errors.unexpected);
      setBusy(false);
      checking.current = false;
    }
  };

  const press = (key: Key) => {
    if (checking.current) return;
    setMessage("");
    if (key === "⌫") {
      setCode((current) => current.slice(0, -1));
    } else if (key === "OK") {
      if (code.length === length) void check(code);
    } else if (code.length < length) {
      const next = code + key;
      setCode(next);
      if (next.length === length) setTimeout(() => void check(next), CHECK_DELAY_MS);
    }
  };

  const onKey = useEffectEvent((event: KeyboardEvent) => {
    const key: Key | null = /^\d$/.test(event.key)
      ? (event.key as Key)
      : event.key === "Backspace"
        ? "⌫"
        : event.key === "Enter"
          ? "OK"
          : null;
    if (key === null) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    press(key);
  });
  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      onKey(event);
    };
    window.addEventListener("keydown", listener, true);
    return () => {
      window.removeEventListener("keydown", listener, true);
    };
  }, []);

  return (
    <Dialog title={title} size="pin" onClose={onClose}>
      {note !== undefined && <p className="note pinnote">{note}</p>}
      <div
        className={`pindots${length > 4 ? " long" : ""}`}
        aria-live="polite"
        aria-label={t.codes.entered(code.length, length)}
      >
        {Array.from({ length }, (_, index) => (
          <span key={index} className={index < code.length ? "on" : ""} />
        ))}
      </div>
      <p className="pinmsg" role="alert">
        {message}
      </p>
      <div className="pinpad">
        {KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className={key === "OK" ? "ok" : undefined}
            aria-label={key === "⌫" ? t.codes.backspace : undefined}
            disabled={busy}
            onClick={() => {
              press(key);
            }}
            data-autofocus={key === "1" ? true : undefined}
          >
            {key}
          </button>
        ))}
      </div>
    </Dialog>
  );
}

export interface CodeRequest {
  permission: CodePurpose;
  /** What the code is for, e.g. "Bestelling annuleren". */
  title: string;
  note?: string | undefined;
}

interface Props extends CodeRequest {
  api: ApiClient;
  onAuthorized: (authorization: Authorization) => void;
  onClose: () => void;
}

/**
 * A personal code (four digits) for one action. The server checks the code and the permission; a
 * person without the right is told who should enter a code instead.
 */
export function CodeDialog({ api, permission, title, note, onAuthorized, onClose }: Props) {
  return (
    <CodeWindow
      title={title}
      note={note}
      length={4}
      submit={async (pin) => {
        onAuthorized(await api.request<Authorization>("POST", "/device/authorizations", { body: { pin, permission } }));
      }}
      onClose={onClose}
    />
  );
}
